import { useState } from "react";
import axios from "axios";
import { Mail, Phone, CheckCircle2, AlertCircle } from "lucide-react";
import Reveal from "./Reveal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY = { name: "", email: "", vision: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setError("");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("success");
      setForm(EMPTY);
    } catch (err) {
      console.error(err);
      setStatus("error");
      setError("Something went wrong. Please try again or email us directly.");
    }
  };

  const inputCls =
    "mt-2 w-full border border-line bg-white px-4 py-3 text-ink focus:outline-none focus:border-primary transition-colors";

  return (
    <section id="contact" className="py-24 md:py-32 bg-white border-t border-line">
      <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16 grid lg:grid-cols-2 gap-12 lg:gap-16">
        <Reveal>
          <p className="text-xs md:text-sm uppercase tracking-[0.2em] font-bold text-accent mb-5">
            Contact
          </p>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight font-medium text-primary leading-[1.05]">
            Let's build opportunities together.
          </h2>
          <p className="mt-6 text-base md:text-lg leading-relaxed text-muted">
            Whether you are a business, investor, logistics partner, healthcare organization or an
            American company looking for representation in Africa, Obled Corporation is ready to
            connect.
          </p>
          <div className="mt-10 grid sm:grid-cols-2 gap-4">
            <a
              href="mailto:contact@obledcorporation.com"
              data-testid="contact-email"
              className="group border border-line p-6 hover:border-accent transition-colors"
            >
              <div className="flex items-center gap-2 text-sm text-muted">
                <Mail size={16} /> Email
              </div>
              <div className="mt-2 text-lg font-medium text-primary group-hover:text-accent break-words">
                contact@obledcorporation.com
              </div>
            </a>
            <a
              href="tel:+15189544701"
              data-testid="contact-phone"
              className="group border border-line p-6 hover:border-accent transition-colors"
            >
              <div className="flex items-center gap-2 text-sm text-muted">
                <Phone size={16} /> Phone
              </div>
              <div className="mt-2 text-lg font-medium text-primary group-hover:text-accent">
                (518) 954-4701
              </div>
            </a>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="border border-line bg-surface p-8 md:p-10 space-y-5"
          >
            <div>
              <label className="text-sm font-medium text-ink">Who are you?</label>
              <input
                required
                data-testid="contact-name-input"
                value={form.name}
                onChange={onChange("name")}
                className={inputCls}
                placeholder="Your name / company"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-ink">Email</label>
              <input
                type="email"
                required
                data-testid="contact-email-input"
                value={form.email}
                onChange={onChange("email")}
                className={inputCls}
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-ink">What is your vision?</label>
              <input
                data-testid="contact-vision-input"
                value={form.vision}
                onChange={onChange("vision")}
                className={inputCls}
                placeholder="Partnership, investment, service..."
              />
            </div>
            <div>
              <label className="text-sm font-medium text-ink">How can Obled help?</label>
              <textarea
                required
                rows={4}
                data-testid="contact-message-input"
                value={form.message}
                onChange={onChange("message")}
                className={`${inputCls} resize-none`}
                placeholder="Tell us about your project"
              />
            </div>
            <button
              type="submit"
              disabled={status === "sending"}
              data-testid="contact-submit-button"
              className="w-full bg-primary text-white py-4 font-medium hover:bg-primary-hover transition-colors disabled:opacity-60"
            >
              {status === "sending" ? "Sending..." : "Send Message"}
            </button>
            {status === "success" && (
              <p
                data-testid="contact-success-message"
                className="flex items-center justify-center gap-2 text-sm text-primary font-medium"
              >
                <CheckCircle2 size={16} className="text-accent" /> Message sent. We'll get back to you shortly.
              </p>
            )}
            {status === "error" && (
              <p
                data-testid="contact-error-message"
                className="flex items-center justify-center gap-2 text-sm text-red-600"
              >
                <AlertCircle size={16} /> {error}
              </p>
            )}
          </form>
        </Reveal>
      </div>
    </section>
  );
}
